import { TaskAdmin } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { IssuesIndicator, StatusBadge } from './StatusBadge';
import {
  computationIssues,
  hasFeature,
  labellersOf,
  reviewersOf,
  splitTable,
  taskStatus,
} from './tasks';
import { DATE_LOCALE } from '../../../../utils/locale';

type AdminLabelTask = TaskAdmin.AdminLabelTask;

function FeatureChips({ task }: { task: AdminLabelTask }) {
  const chips = [
    hasFeature(task, 'iteration') && 'Iteration',
    hasFeature(task, 'predictions') && 'Predictions',
  ].filter(Boolean) as string[];
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map(chip => (
        <span
          key={chip}
          className="whitespace-nowrap rounded-full bg-dl-tint px-2 py-0.5 text-xs font-medium text-dl-accent"
        >
          {chip}
        </span>
      ))}
    </span>
  );
}

/** Who is on the task: the labellers by name when they are few, the reviewers counted. */
function People({ task }: { task: AdminLabelTask }) {
  const labellers = labellersOf(task);
  const reviewers = reviewersOf(task);
  const short = (email: string) => email.split('@')[0];
  const title = [
    labellers.length && `Labellers: ${labellers.join(', ')}`,
    reviewers.length && `Reviewers: ${reviewers.join(', ')}`,
  ]
    .filter(Boolean)
    .join('\n');
  if (!task.taskers) {
    return (
      <span className="text-sm text-dl-faint">
        {reviewers.length ? `${reviewers.length} reviewer(s)` : '—'}
      </span>
    );
  }
  return (
    <span className="block max-w-[14rem] text-sm" title={title || undefined}>
      <span className="block truncate">
        {labellers.length === 0
          ? 'No labeller'
          : labellers.length <= 2
            ? labellers.map(short).join(', ')
            : `${labellers.length} labellers`}
      </span>
      {reviewers.length > 0 && (
        <span className="block truncate text-dl-faint">
          Review:{' '}
          {reviewers.length <= 2
            ? reviewers.map(short).join(', ')
            : reviewers.length}
        </span>
      )}
    </span>
  );
}

export function formatDay(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(DATE_LOCALE, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

/** The rows of one page (of one group): clicking anywhere on a row opens its task (through its name's button). */
export function TasksTable({
  tasks,
  onOpen,
  openTaskId,
}: {
  tasks: AdminLabelTask[];
  onOpen: (taskId: number) => void;
  openTaskId: number | null;
}) {
  return (
    // contain: the layout's surface is min-content wide, so a wide table would widen the page, not scroll
    <div className="-mx-6 overflow-x-auto [contain:inline-size]">
      <table className="w-full table-auto">
        <thead>
          <tr>
            <TableHeadTh className="pl-6">Task</TableHeadTh>
            <TableHeadTh>Status</TableHeadTh>
            <TableHeadTh>Dataset</TableHeadTh>
            <TableHeadTh>People</TableHeadTh>
            <TableHeadTh>Setup</TableHeadTh>
            <TableHeadTh className="text-right">Rev.</TableHeadTh>
            <TableHeadTh className="pr-6">Created</TableHeadTh>
          </tr>
        </thead>
        <tbody>
          {tasks.map(task => {
            const dataset = splitTable(task.dataset);
            return (
              <tr
                key={task.taskId}
                className={classNames('group relative cursor-pointer', {
                  'bg-dl-tint': openTaskId === task.taskId,
                })}
              >
                <TableBodyTd className="max-w-md pl-6">
                  <button
                    type="button"
                    onClick={() => onOpen(task.taskId)}
                    className="block text-left text-lg font-semibold text-dl-text transition after:absolute after:inset-0 after:content-[''] group-hover:text-dl-accent"
                  >
                    {task.name}
                  </button>
                  <span className="font-mono text-sm text-dl-faint">
                    #{task.taskId}
                  </span>
                  {task.description && (
                    <span className="line-clamp-2 text-sm text-dl-muted">
                      {task.description}
                    </span>
                  )}
                </TableBodyTd>
                <TableBodyTd>
                  <span className="flex flex-col items-start gap-1">
                    <StatusBadge status={taskStatus(task)} />
                    <IssuesIndicator issues={computationIssues(task)} />
                  </span>
                </TableBodyTd>
                <TableBodyTd
                  className="max-w-[18rem] font-mono text-sm"
                  title={task.dataset ?? undefined}
                >
                  <span className="block truncate">{dataset.table || '—'}</span>
                  {dataset.location && (
                    <span className="block truncate text-xs text-dl-faint">
                      {dataset.location}
                    </span>
                  )}
                </TableBodyTd>
                <TableBodyTd>
                  <People task={task} />
                </TableBodyTd>
                <TableBodyTd>
                  <FeatureChips task={task} />
                </TableBodyTd>
                <TableBodyTd className="text-right tabular-nums">
                  {task.revision}
                </TableBodyTd>
                <TableBodyTd className="whitespace-nowrap pr-6">
                  {formatDay(task.createdAt)}
                  <span
                    className="block max-w-[12rem] truncate text-sm text-dl-faint"
                    title={task.createdBy}
                  >
                    {task.createdBy}
                  </span>
                </TableBodyTd>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
